"use client";

import { useState, useEffect } from "react";
import Lottie from "lottie-react";
import Link from "next/link";
import notFound from "@/types/animations/404pill.json";
import notFound2 from "@/types/animations/Component-1.json";
interface NotFoundProps {
  title?: string;
  message?: string;
  errorCode?: string;
  backLink?: string;
  backLinkText?: string;
  secondaryLink?: string;
  secondaryLinkText?: string;
  showQuickLinks?: boolean;
}

// 404 SVG Component
const NotFoundSVG = () => (
  <svg
    className="w-full h-auto max-w-[300px] sm:max-w-[350px] md:max-w-[400px] lg:max-w-[450px] xl:max-w-[500px]"
    viewBox="0 0 554 311"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M92.9101 269C90.217 269 88.8705 267.653 88.8705 264.96V201.619H4.03901C1.34595 201.619 -0.000580895 200.273 -0.000580895 197.58V57.6482C-0.000580895 54.9551 1.34595 53.6086 4.03901 53.6086H49.7672C52.4603 53.6086 53.8068 54.9551 53.8068 57.6482V158.638H88.8705V57.6482C88.8705 54.9551 90.217 53.6086 92.9101 53.6086H138.638C141.331 53.6086 142.678 54.9551 142.678 57.6482V264.96C142.678 267.653 141.331 269 138.638 269H92.9101Z"
      fill="#072866"
    />
    <path
      d="M503.91 269C501.217 269 499.87 267.653 499.87 264.96V201.619H415.039C412.346 201.619 410.999 200.273 410.999 197.58V57.6482C410.999 54.9551 412.346 53.6086 415.039 53.6086H460.767C463.46 53.6086 464.807 54.9551 464.807 57.6482V158.638H499.87V57.6482C499.87 54.9551 501.217 53.6086 503.91 53.6086H549.638C552.331 53.6086 553.678 54.9551 553.678 57.6482V264.96C553.678 267.653 552.331 269 549.638 269H503.91Z"
      fill="#072866"
    />
    <path
      d="M226.569 48.0592C214.923 60.8972 208.972 77.5054 209.821 94.8077L216.001 221.714C216.847 239.027 224.377 254.979 237.212 266.621C263.711 290.659 304.829 288.657 328.867 262.158C340.519 249.312 346.464 232.712 345.614 215.409L339.438 88.5057C338.598 71.1861 331.061 55.2413 318.223 43.5956C291.724 19.5581 250.607 21.5604 226.569 48.0592ZM243.854 177.959L245.91 220.258C246.364 229.58 250.423 238.172 257.333 244.44C259.377 246.294 259.53 249.447 257.676 251.491C255.823 253.534 252.669 253.688 250.626 251.834C241.737 243.771 236.521 232.734 235.941 220.744L233.884 178.445C233.75 175.69 235.872 173.351 238.626 173.217C241.38 173.082 243.72 175.205 243.854 177.959ZM319.498 89.4767L322.342 147.941L232.608 152.306L229.761 93.8364C229.174 81.8531 233.293 70.3615 241.356 61.4731C257.999 43.1259 286.462 41.7401 304.809 58.3831C313.698 66.446 318.918 77.4861 319.498 89.4767Z"
      fill="#FC7207"
    />
  </svg>
);

export default function NotFoundComponent({
  title = "Oops — we couldn't reveal that page.",
  message = "This page doesn't exist, but your pharmacy's digital success story can. Let's build something great together.",
  backLink = "/",
  backLinkText = "Go home",
  secondaryLink = "/contact",
  secondaryLinkText = "Contact Us",
}: NotFoundProps) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024); // lg breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Use notFound2 for mobile, notFound for desktop
  const animationData = isMobile ? notFound2 : notFound;

  return (
    <div className="h-screen  bg-white flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-[70px] w-full flex xl:flex-row flex-col justify-center h-screen lg:gap-8 gap-4 items-center">
        {/* Left Content */}
        <div className="w-full flex flex-col max-xl:items-center justify-center xl:w-[45%] xl:self-end order-2 xl:order-1  xl:mb-[100px] text-center xl:text-left">
          <h1 className="text-2xl md:text-5xl lg:text-5xl font-semibold text-black mb-[13px] leading-normal">
            {title}
          </h1>
          <p className=" text-base md:text-2xl font-medium text-black mb-[10px] lg:mb-[29px] leading-normal max-w-xl">
            {message}
          </p>

          {/* Action Buttons */}
          <div className="flex  gap-[29px]">
            <Link
              href={backLink}
              className="inline-flex   items-center justify-center px-6 py-4 bg-[#FC7207] hover:bg-[#E6610A] text-white font-bold text-[16px] rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl"
            >
              {backLinkText}
            </Link>

            {secondaryLink && (
              <Link
                href={secondaryLink}
                className="inline-flex items-center justify-center px-6 py-4 bg-white border border-[##F6842B] text-[#FC7207] hover:bg-[#FC7207] hover:text-white font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                {secondaryLinkText}
              </Link>
            )}
          </div>
        </div>

        {/* Right 404 Graphic */}
        <div className="w-full justify-center  xl:w-[60%] xl:self-start xl:justify-end order-1 xl:order-2 flex ">
          <div className="size-[500px] lg:size-[300px] xl:size-[800px] max-h-[800px] ">
            <Lottie
              animationData={animationData}
              loop={true}
              autoplay={true}
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
